import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Modal,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
} from "@mui/material";
import { ResponsiveBar } from "@nivo/bar";
import { useCompanyVolume, VolumeTimeframe } from "../hooks/useCompanyVolume";
import { useTheme } from "@mui/material/styles";
import { formatNumber } from "../utils/formatNumber";
import {
  evenlySpacedTickValues,
  evenlySpacedTimeTickValues,
} from "../utils/evenlySpacedTickValues";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { getChartColors } from "../constants/chartColors";
import ErrorDisplay from "../../../common/ErrorDisplay";
import ChartTooltip, { ChartTooltipRow } from "./shared/ChartTooltip";
import ChartTooltipPortal from "./shared/ChartTooltipPortal";
import { modalBackdrop } from "../../../../theme";
import { workspaceSwitchSx } from "../../../Dashboard/overviewPanel";
import { hapticSelection } from "../../../../lib/haptics";
import { Quote } from "../hooks/useCompanyQuote";
import { Theme } from "@mui/material/styles";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";
import { getExchangeTimezone } from "../../../../utils/exchangeTimezone";
import { useExchangeTradingMinutes } from "../hooks/useExchangeTradingMinutes";

// Caps how wide a single volume bar can grow, so a session with only one or
// two buckets doesn't render as a slab across the plot. Matches the width used
// by the revenue projection bars.
const VOLUME_MAX_BAR_WIDTH = 64;

// Flat UI Color System - Theme Aware
const getFlatUIColors = (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";

  return {
    // Primary Actions & Growth (similar in both themes)
    primary: "#22c55e", // Main green for positive actions
    primaryHover: "#16a34a", // Darker green for hover states

    // Negative/Decline States (similar in both themes)
    negative: "#ef4444", // Clear red for losses/negative data
    negativeHover: "#dc2626", // Darker red for hover

    // Neutral Interface (theme-dependent)
    background: isDark ? "#1a1a1a" : "#ffffff", // Dark grey-black or pure white
    surface: isDark ? "#212121" : "#f8fafc", // Slightly lighter dark or subtle gray
    border: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0", // Subtle white lines or light gray
    borderSubtle: isDark ? "rgba(255,255,255,0.05)" : "#f1f5f9", // Very subtle borders

    // Typography (inverted for dark theme)
    textPrimary: isDark ? "#e4e6ea" : "#0f172a", // Light or dark text
    textSecondary: isDark ? "#9ca3af" : "#64748b", // Medium contrast text
    textMuted: isDark ? "#6b7280" : "#94a3b8", // Low contrast text

    // Accent/Information (slightly adjusted for dark)
    accent: isDark ? "#60a5fa" : "#3b82f6", // Brighter blue in dark mode
    warning: isDark ? "#fbbf24" : "#f59e0b", // Brighter orange in dark mode
  };
};

dayjs.extend(utc);
dayjs.extend(timezone);

interface VolumeChartProps {
  ticker: string;
  company?: Quote;
  height?: number | string;
  isModal?: boolean;
  onOpenModal?: () => void;
  tradingDate?: string;
  renderCard?: boolean;
  showFullscreen?: boolean;
}

interface ChartDataPoint {
  time: string;
  volume: number;
  originalDate: string;
  localTime: string;
  etTime: string;
  [key: string]: any;
}

// Volume sibling of the 1D price chart's fillIntradayMinuteGrid. FMP returns a
// 15-minute bucket only when that window traded, so an illiquid name comes back
// gappy and the band-scale labels land on arbitrary buckets (14:30 · 15:15 ·
// 16:00 …). Resampling onto a continuous 15-minute grid restores index == time,
// so evenlySpacedTimeTickValues lands clean, evenly-spaced, round-hour labels
// that match the price chart. A no-trade window genuinely has zero volume, so
// gaps are filled with a zero bar — NOT a carried-forward value (the key
// difference from the price chart, where price persists). Only internal gaps
// are filled: the series still starts and ends on the first/last real bucket.
// Used for rendering only; volume metrics stay computed on the real buckets.
const fillVolumeBucketGrid = (
  buckets: ChartDataPoint[],
  exchangeTimezone: string,
  userTimezone: string,
  stepMinutes: number
): ChartDataPoint[] => {
  if (buckets.length < 2) return buckets;

  const bucketKey = (date: string) =>
    dayjs.tz(date, exchangeTimezone).format("YYYY-MM-DD HH:mm");
  const byBucket = new Map<string, ChartDataPoint>();
  buckets.forEach((b) => byBucket.set(bucketKey(b.originalDate), b));

  const start = dayjs
    .tz(buckets[0].originalDate, exchangeTimezone)
    .startOf("minute");
  const end = dayjs
    .tz(buckets[buckets.length - 1].originalDate, exchangeTimezone)
    .startOf("minute");

  // An unparseable date makes `!t.isAfter(end)` permanently true (infinite
  // loop → tab hang) and a misparsed-but-valid one makes the span absurd —
  // bail to the raw buckets in both cases.
  if (!start.isValid() || !end.isValid() || end.diff(start, "minute") / stepMinutes > 50000) {
    return buckets;
  }

  const filled: ChartDataPoint[] = [];
  for (let t = start; !t.isAfter(end); t = t.add(stepMinutes, "minute")) {
    const real = byBucket.get(t.format("YYYY-MM-DD HH:mm"));
    if (real) {
      filled.push(real);
      continue;
    }
    const localTime = t.tz(userTimezone);
    filled.push({
      time: localTime.format("HH:mm"),
      volume: 0,
      originalDate: t.format("YYYY-MM-DD HH:mm:ss"),
      localTime: localTime.format("HH:mm"),
      etTime: t.format("HH:mm"),
    });
  }
  return filled;
};

const VolumeChart: React.FC<VolumeChartProps> = ({
  ticker,
  company,
  height = 400,
  isModal = false,
  onOpenModal,
  tradingDate,
  renderCard = true,
  showFullscreen = false,
}) => {
  const theme = useTheme();
  const flatUIColors = getFlatUIColors(theme);
  const [timeframe, setTimeframe] = useState<VolumeTimeframe>("15min");
  const [modalOpen, setModalOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [measuredWidth, setMeasuredWidth] = useState(0);
  const { exchange } = useStockCurrency();
  const exchangeTimezone = getExchangeTimezone(exchange);
  const { openMinutes, closeMinutes } = useExchangeTradingMinutes(exchange);
  const { data: volumeResponse, isLoading, error } = useCompanyVolume(
    ticker,
    timeframe,
    tradingDate
  );
  
  // Handle different response formats based on timeframe
  let data: any[] = [];
  let fallbackUsed = false;
  let actualDate: string | undefined;
  
  if (timeframe === '15min') {
    // For 15min, response is VolumeDataWithMetadata
    if (volumeResponse && typeof volumeResponse === 'object' && 'data' in volumeResponse) {
      data = volumeResponse.data;
      fallbackUsed = volumeResponse.fallbackUsed;
      actualDate = volumeResponse.actualDate;
    }
  } else {
    // For monthly, response is DailyVolumeRecord[]
    if (Array.isArray(volumeResponse)) {
      data = volumeResponse;
    }
  }
  const colors = getChartColors(theme.palette.mode === "dark");

  const chartData = useMemo(() => {
    if (!data || (Array.isArray(data) && data.length === 0)) return [];

    if (timeframe === "15min") {
      // Handle intraday 15-minute data with timezone conversion
      const userTimezone = dayjs.tz.guess();

      // Filter to trading hours and process data
      const tradingHourData = data
        .filter((item: any) => {
          // Parse the date in exchange timezone first
          const itemTimeExchange = dayjs.tz(item.date, exchangeTimezone);
          const hour = itemTimeExchange.hour();
          const minute = itemTimeExchange.minute();

          // Clip to the exchange's real regular session (US 09:30–16:00,
          // LSE 08:00–16:30, etc.) resolved from FMP's live market hours.
          const timeInMinutes = hour * 60 + minute;

          return timeInMinutes >= openMinutes && timeInMinutes <= closeMinutes;
        })
        .sort((a: any, b: any) => dayjs(a.date).unix() - dayjs(b.date).unix()) // Sort chronologically
        .map((item: any): ChartDataPoint => {
          // Convert exchange time to user's local timezone for display
          const exchangeTime = dayjs.tz(item.date, exchangeTimezone);
          const localTime = exchangeTime.tz(userTimezone);

          return {
            time: localTime.format("HH:mm"),
            volume: item.volume,
            originalDate: item.date,
            localTime: localTime.format("HH:mm"),
            etTime: exchangeTime.format("HH:mm"),
          };
        });

      return tradingHourData;
    } else {
      // Handle monthly daily volume data
      const dailyData = data
        .sort((a: any, b: any) => dayjs(a.date).unix() - dayjs(b.date).unix()) // Sort chronologically
        .map(
          (item: any): ChartDataPoint => ({
            time: dayjs(item.date).format("MMM DD"),
            volume: item.volume,
            originalDate: item.date,
            localTime: dayjs(item.date).format("MMM DD"),
            etTime: dayjs(item.date).format("MMM DD"),
          })
        );

      return dailyData;
    }
  }, [data, timeframe, exchangeTimezone, openMinutes, closeMinutes]);

  // Calculate volume metrics
  const volumeMetrics = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;

    const totalVolume = chartData.reduce((sum, item) => sum + item.volume, 0);
    // Volume is a count of whole shares — never present floating-point
    // averages or peaks to the user. Round here so downstream formatting
    // can't display 124.92153739130435 even if formatNumber's <1000
    // branch is hit.
    const averageVolume = Math.round(totalVolume / chartData.length);
    const maxVolume = Math.round(Math.max(...chartData.map((item) => item.volume)));
    const maxVolumeTime = chartData.find(
      (item) => item.volume === maxVolume
    )?.time;

    return {
      totalVolume: Math.round(totalVolume),
      averageVolume,
      maxVolume,
      maxVolumeTime,
    };
  }, [chartData]);

  // Daily (15-min) view: resample onto a continuous 15-minute grid so the bars
  // sit at their true time positions and the axis labels space evenly on the
  // hour — matching the 1D price chart. Monthly is left untouched. Rendering
  // only; volumeMetrics above stays on the real buckets so totals/averages are
  // unaffected by the zero-filled gaps.
  const griddedChartData = useMemo(() => {
    if (timeframe !== "15min") return chartData;
    const userTimezone = dayjs.tz.guess();
    return fillVolumeBucketGrid(chartData, exchangeTimezone, userTimezone, 15);
  }, [chartData, timeframe, exchangeTimezone]);

  // Nivo sizes bars as a fraction of the band, so a single bucket — the state
  // of play right after the open — stretches one bar across the whole plot.
  // Measure the plot and convert a max pixel width into the padding that
  // enforces it, matching GenericFinancialBarChart.
  useEffect(() => {
    const el = chartContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setMeasuredWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [griddedChartData.length]);

  const basePadding = 0.15;
  const effectivePadding = (() => {
    if (measuredWidth <= 0 || griddedChartData.length === 0) return basePadding;
    const leftMargin = isMobile ? 55 : 80;
    const rightMargin = isMobile ? 10 : 20;
    const innerWidth = measuredWidth - leftMargin - rightMargin;
    if (innerWidth <= 0) return basePadding;
    const required = 1 - (VOLUME_MAX_BAR_WIDTH * griddedChartData.length) / innerWidth;
    return Math.min(0.92, Math.max(basePadding, required));
  })();

  // Mirror the 1D price chart: on the Daily view, place labels on a consistent
  // clock grid (hour boundaries) so they're evenly spaced and read as round
  // times. Monthly keeps the plain index-based spacing for its date labels.
  const axisBottomTickValues = useMemo(() => {
    const xValues = griddedChartData.map((d) => String(d.time));
    if (xValues.length === 0) return undefined;

    if (timeframe === "15min") {
      const minutesOfDay = (value: string) => {
        const [hours, minutes] = value.split(":").map(Number);
        return Number.isFinite(hours) && Number.isFinite(minutes)
          ? hours * 60 + minutes
          : 0;
      };
      return evenlySpacedTimeTickValues(xValues, minutesOfDay, isMobile);
    }

    return evenlySpacedTickValues(xValues, isMobile);
  }, [griddedChartData, timeframe, isMobile]);


  if (isLoading) {
    return (
      <Card
        elevation={0}
        sx={{ backgroundColor: theme.palette.mode === "dark" ? "#212121" : "#f5f5f5" }}
      >
        <CardContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height={height}
          >
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card
        elevation={0}
        sx={{ backgroundColor: theme.palette.mode === "dark" ? "#212121" : "#f5f5f5" }}
      >
        <CardContent>
          <ErrorDisplay
            error={error}
            onRetry={() => window.location.reload()}
            fullWidth
          />
        </CardContent>
      </Card>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <Card
        elevation={0}
        sx={{ backgroundColor: theme.palette.mode === "dark" ? "#212121" : "#f5f5f5" }}
      >
        <CardContent>
          <Alert severity="info">
            No volume data available for the selected trading day.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const chartContent = (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: height === "100%" ? "100%" : height,
            minHeight: 0,
          }}
        >
        {/* Header: centred title, controls on the right (inline on mobile).
            Three columns keep the row as tall as the controls, so the title
            and the switch share one centre line and the spacing below matches
            the other charts. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isMobile ? "auto auto" : "1fr auto 1fr",
            alignItems: "center",
            justifyContent: isMobile ? "space-between" : undefined,
            gap: 1,
            mb: 2,
          }}
        >
          <Typography
            variant="subtitle1"
            component="h2"
            sx={{
              gridColumn: isMobile ? "auto" : 2,
              fontWeight: 600,
              color: "text.primary",
              fontSize: isModal ? "1.25rem" : "1rem",
              textAlign: "center",
            }}
          >
            Trading Volume
          </Typography>

          <Box
            sx={{
              gridColumn: isMobile ? "auto" : 3,
              justifySelf: "end",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <ToggleButtonGroup
              value={timeframe}
              exclusive
              size="small"
              aria-label="Trading Volume view"
              onChange={(_, value: VolumeTimeframe | null) => {
                if (value === null || value === timeframe) return;
                hapticSelection();
                setTimeframe(value);
              }}
              sx={workspaceSwitchSx}
            >
              <ToggleButton value="15min">Daily</ToggleButton>
              <ToggleButton value="monthly">Monthly</ToggleButton>
            </ToggleButtonGroup>
            {showFullscreen && !isModal && !isMobile && (
              <IconButton
                aria-label="Expand Trading Volume chart"
                onClick={onOpenModal || (() => setModalOpen(true))}
                size="small"
                sx={{ color: "primary.main" }}
              >
                <FullscreenIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        <Box
          ref={chartContainerRef}
          sx={{
            flex: 1,
            minHeight: 0,
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.02)"
                : "rgba(0, 0, 0, 0.015)",
            borderRadius: 1,
          }}
        >
          <ResponsiveBar
            data={griddedChartData}
            keys={["volume"]}
            indexBy="time"
            animate={false}
            margin={{
              top: isMobile ? 12 : 16,
              right: isMobile ? 10 : 20,
              bottom: isMobile ? 56 : 44,
              left: isMobile ? 55 : 80,
            }}
            padding={effectivePadding}
            valueScale={{ type: "linear", min: 0 }}
            indexScale={{ type: "band", round: true }}
            colors={[colors.positive]}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 0,
              tickPadding: isMobile ? 8 : 12,
              // Daily labels are now short hour marks, so keep them flat on
              // desktop to match the price chart. Mobile stays rotated for room;
              // Monthly date labels keep a slight tilt to avoid overlap.
              tickRotation: isMobile ? -45 : (timeframe === "15min" ? 0 : -25),
              tickValues: axisBottomTickValues,
            }}
            axisLeft={{
              tickSize: 0,
              tickPadding: isMobile ? 8 : 12,
              tickRotation: 0,
              format: (value) => formatNumber(value),
              tickValues: isMobile ? 4 : 5,
            }}
            enableLabel={false}
            enableGridX={!isMobile}
            gridXValues={axisBottomTickValues}
            enableGridY={true}
            gridYValues={5}
            borderRadius={0}
            tooltip={({ data, value }) => {
              const point = data as ChartDataPoint;
              const volume = value as number;
              const isIntraday = timeframe === "15min";

              // Intraday buckets are 15 minutes wide, so the axis label alone
              // ("14:30") understates what the bar covers — show the window.
              const header = (() => {
                if (!isIntraday) return dayjs(point.originalDate).format("ddd, D MMM YYYY");
                const start = dayjs
                  .tz(point.originalDate, exchangeTimezone)
                  .tz(dayjs.tz.guess());
                return `${start.format("HH:mm")} – ${start.add(15, "minute").format("HH:mm")}`;
              })();

              const rows: ChartTooltipRow[] = [
                { label: "Volume", value: `${formatNumber(volume)} shares` },
              ];

              // Zero-volume buckets are the grid fill for windows that never
              // traded; the comparisons below would just read "-100%".
              if (volume > 0 && volumeMetrics) {
                if (volumeMetrics.totalVolume > 0) {
                  rows.push({
                    label: isIntraday ? "Share of day" : "Share of period",
                    value: `${((volume / volumeMetrics.totalVolume) * 100).toFixed(1)}%`,
                  });
                }
                if (volumeMetrics.averageVolume > 0) {
                  const delta = (volume / volumeMetrics.averageVolume - 1) * 100;
                  rows.push({
                    label: "vs average",
                    value: `${delta >= 0 ? "+" : ""}${delta.toFixed(0)}%`,
                    color: delta >= 0 ? colors.positive : colors.negative,
                  });
                }
              }

              // Only worth the row when the user isn't in the exchange's own
              // timezone — otherwise it repeats the header.
              if (isIntraday && point.etTime !== point.localTime) {
                rows.push({ label: "Exchange time", value: point.etTime });
              }

              return (
                <ChartTooltipPortal>
                  <ChartTooltip header={header} headerColor={colors.positive} rows={rows} />
                </ChartTooltipPortal>
              );
            }}
            theme={{
              background: "transparent",
              text: {
                fontSize: isMobile ? 10 : 12,
                fill: flatUIColors.textMuted,
                outlineWidth: 0,
                outlineColor: "transparent",
              },
              axis: {
                domain: {
                  line: {
                    stroke: "transparent",
                    strokeWidth: 0,
                  },
                },
                ticks: {
                  line: {
                    stroke: "transparent",
                    strokeWidth: 0,
                  },
                  text: {
                    fontSize: isMobile ? 10 : 11,
                    fill: flatUIColors.textPrimary,
                    fontWeight: 600,
                  },
                },
              },
              grid: {
                line: {
                  stroke: theme.palette.divider,
                  strokeWidth: 1,
                  strokeOpacity: 0.2,
                },
              },
            }}
            isInteractive
            defs={[
              {
                id: 'volumeGradient',
                type: 'linearGradient',
                colors: [
                  { offset: 0, color: colors.positive, opacity: 1 },
                  { offset: 50, color: colors.positive, opacity: 0.6 },
                  { offset: 100, color: colors.positive, opacity: 0.3 },
                ],
              },
            ]}
            fill={[{ match: '*', id: 'volumeGradient' }]}
          />
        </Box>

        {/* Period summary: one quiet line, there when wanted and easy to
            skip. Peak is the only figure that earns the accent colour. */}
        {volumeMetrics && (
          <Box
            sx={{
              mt: 1.25,
              flexShrink: 0,
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              columnGap: isMobile ? 2 : 3.5,
              rowGap: 0.5,
            }}
          >
            {[
              { label: "Total", value: formatNumber(volumeMetrics.totalVolume) },
              { label: "Average", value: formatNumber(volumeMetrics.averageVolume) },
              {
                label: "Peak",
                value: formatNumber(volumeMetrics.maxVolume),
                detail: volumeMetrics.maxVolumeTime,
                accent: true,
              },
            ].map((item) => (
              <Typography
                key={item.label}
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "baseline",
                  gap: 0.75,
                  fontSize: "0.75rem",
                  color: "text.secondary",
                  whiteSpace: "nowrap",
                }}
              >
                {item.label}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.8125rem",
                    color: item.accent ? "primary.main" : "text.primary",
                  }}
                >
                  {item.value}
                </Box>
                {item.detail && <span>at {item.detail}</span>}
              </Typography>
            ))}
          </Box>
        )}
        {timeframe === "15min" && fallbackUsed && actualDate && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Chip
              label="Showing data from the last trading day"
              size="small"
              sx={{
                fontSize: "0.75rem",
                height: 24,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 193, 7, 0.15)"
                    : "rgba(255, 193, 7, 0.1)",
                color: theme.palette.mode === "dark" ? "#FFC107" : "#E65100",
                border: `1px solid ${
                  theme.palette.mode === "dark"
                    ? "rgba(255, 193, 7, 0.3)"
                    : "rgba(255, 193, 7, 0.4)"
                }`,
              }}
            />
          </Box>
        )}
        </Box>
  );

  if (!renderCard) {
    return (
      <>
        {chartContent}
        
        {/* Internal Fullscreen Modal */}
        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
          slotProps={{
            backdrop: {
              sx: modalBackdrop,
            },
          }}
        >
          <Box
            sx={{
              width: "90vw",
              height: "90vh",
              position: "relative",
              overflow: "hidden",
              backgroundColor: theme.palette.mode === "dark" ? "#1a1a1a" : "#ffffff",
              borderRadius: 4,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 1000,
                width: 40,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconButton
                onClick={() => setModalOpen(false)}
                sx={{ 
                  width: 32,
                  height: 32,
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Box
              sx={{
                height: "100%",
                p: 2,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <VolumeChart
                ticker={ticker}
                company={company}
                height="100%"
                isModal={true}
                tradingDate={tradingDate}
                renderCard={false}
              />
            </Box>
          </Box>
        </Modal>
      </>
    );
  }

  return (
    <Card
      elevation={0}
      sx={{
        backgroundColor: theme.palette.mode === "dark" ? "#212121" : "#f5f5f5",
        ...(isModal && {
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }),
      }}
    >
      <CardContent
        sx={{
          ...(isModal && {
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }),
        }}
      >
        {chartContent}
      </CardContent>

      {/* Fullscreen Modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
        slotProps={{
          backdrop: {
            sx: modalBackdrop,
          },
        }}
      >
        <Box
          sx={{
            width: "90vw",
            height: "90vh",
            position: "relative",
            overflow: "hidden",
            backgroundColor: theme.palette.mode === "dark" ? "#1a1a1a" : "#ffffff",
            borderRadius: 4,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1000,
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconButton
              onClick={() => setModalOpen(false)}
              sx={{ 
                width: 32,
                height: 32,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Box sx={{ height: "100%", p: 2, display: 'flex', flexDirection: 'column' }}>
            <VolumeChart ticker={ticker} company={company} height="100%" isModal={true} renderCard={false} tradingDate={tradingDate} />
          </Box>
        </Box>
      </Modal>
    </Card>
  );
};

// Modal wrapper component
interface VolumeChartModalProps {
  ticker: string;
  company?: Quote;
  open: boolean;
  onClose: () => void;
  tradingDate?: string;
}

export const VolumeChartModal: React.FC<VolumeChartModalProps> = ({
  ticker,
  company,
  open,
  onClose,
  tradingDate,
}) => {
  const theme = useTheme();

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
      slotProps={{
        backdrop: {
          sx: modalBackdrop,
        },
      }}
    >
      <Box
        sx={{
          width: "90vw",
          height: "90vh",
          position: "relative",
          overflow: "hidden",
          backgroundColor: theme.palette.mode === "dark" ? "#1a1a1a" : "#ffffff",
          borderRadius: 4,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 1000,
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconButton 
            onClick={onClose} 
            sx={{ 
              width: 32,
              height: 32,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            height: "100%",
            p: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <VolumeChart
            ticker={ticker}
            company={company}
            height="100%"
            isModal={true}
            tradingDate={tradingDate}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default React.memo(VolumeChart);
